import {RestService} from "@/services/rest/RestService";
import {MailingMailTemplateApi} from "@/api/pharma-master-mailing-template-library/apis/mailing-mail-template-api";
import {
    MailingMailTemplateOverviewResultsRestDto,
    MailingMailTemplateRestDto
} from "@/api/pharma-master-mailing-template-library/models";
import {AxiosResponse} from "axios";
import {CacheService} from "@/services/cache/CacheService";
import {CustomerContext} from "@/context/CustomerContext";
import {CacheKeyEnum} from "@/services/cache/CacheKeyEnum";

export class MailingMailTemplateRestService extends RestService {

    private apiService: MailingMailTemplateApi;

    protected constructor() {
        super();
        this.apiService = new MailingMailTemplateApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().masterUrl);
    }

    public async findMailingMailTemplates(moduleCode: string): Promise<MailingMailTemplateOverviewResultsRestDto> {
        // UI omits filter_library_code for now -> use default library code 'DIGITAL_PHARMA'
        const libraryCode = 'DIGITAL_PHARMA';
        const nonNullCustomerCode = CustomerContext.getCustomerCode();
        const cacheKey = this.getOverviewCacheKey(moduleCode, libraryCode);

        let promise: Promise<AxiosResponse<MailingMailTemplateOverviewResultsRestDto>> =
            CacheService.getInstance().get(cacheKey, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<MailingMailTemplateOverviewResultsRestDto> = await promise;
            return result.data;
        }

        promise = this.apiService.findMailingMailTemplates(moduleCode, libraryCode);
        CacheService.getInstance().set(cacheKey, promise, nonNullCustomerCode);

        const result: AxiosResponse<MailingMailTemplateOverviewResultsRestDto> = await promise;
        return result.data;
    }

    public async getMailingMailTemplate(id: string, moduleCode: string): Promise<MailingMailTemplateRestDto> {
        const nonNullCustomerCode = CustomerContext.getCustomerCode();
        const cacheKey = this.getDetailCacheKey(moduleCode, id);

        let promise: Promise<AxiosResponse<MailingMailTemplateRestDto>> =
            CacheService.getInstance().get(cacheKey, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<MailingMailTemplateRestDto> = await promise;
            return result.data;
        }

        promise = this.apiService.getMailingMailTemplate(id, moduleCode);
        CacheService.getInstance().set(cacheKey, promise, nonNullCustomerCode);

        const result: AxiosResponse<MailingMailTemplateRestDto> = await promise;
        return result.data;
    }

    public async createMailingMailTemplate(body: MailingMailTemplateRestDto, moduleCode: string): Promise<void> {
        await this.apiService.createMailingMailTemplate(body, moduleCode);
        CacheService.getInstance().invalidateByPrefix(this.getOverviewCacheKey(moduleCode), CustomerContext.getCustomerCode());
    }

    public async updateMailingMailTemplate(id: string, body: MailingMailTemplateRestDto, moduleCode: string): Promise<void> {
        const nonNullCustomerCode = CustomerContext.getCustomerCode();
        await this.apiService.updateMailingMailTemplate(body, moduleCode, id);
        CacheService.getInstance().invalidate(this.getDetailCacheKey(moduleCode, id), nonNullCustomerCode);
        CacheService.getInstance().invalidateByPrefix(this.getOverviewCacheKey(moduleCode), nonNullCustomerCode);
    }

    private getDetailCacheKey(moduleCode: string, templateId: string): string {
        return `${CacheKeyEnum.MAILING_MAIL_TEMPLATES}-${moduleCode}-${templateId}`;
    }

    private getOverviewCacheKey(moduleCode: string, libraryCode: string | null = null): string {
        if (libraryCode) {
            return `${CacheKeyEnum.MAILING_MAIL_TEMPLATES}-${moduleCode}-${libraryCode}`;
        }

        return `${CacheKeyEnum.MAILING_MAIL_TEMPLATES}-${moduleCode}`;
    }
}
